/**
 * help/help-utils.js
 *
 * Utilidades para buscar y recuperar ayuda por pantalla.
 */

import { HELP_SECTIONS } from './help-index.js'

/** @param {string} value */
export const normalizeSearchTerm = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

/** @param {import('./help-index.js').HelpSidebarItem} item */
const sidebarItemSearchText = (item) => {
  const parts = [item.label, item.description]
  ;(item.detail?.sections || []).forEach((section) => {
    parts.push(section.title, section.intro || '')
    parts.push(...(section.bullets || []), ...(section.paragraphs || []))
    ;(section.fields || []).forEach((field) => {
      parts.push(field.label, field.description)
    })
  })
  return normalizeSearchTerm(parts.join(' '))
}

/** @param {import('./help-index.js').HelpSidebarItem} item @param {string} term */
const sidebarItemMatchesSearch = (item, term) => sidebarItemSearchText(item).includes(term)

/** @param {import('./help-index.js').HelpSidebarItem} item @param {string} query */
export const sidebarItemMatchesQuery = (item, query) =>
  sidebarItemMatchesSearch(item, normalizeSearchTerm(query))

export const getSidebarItemKey = (screenId, itemId) => `${screenId}::${itemId}`

/**
 * @param {string} screenId
 * @returns {{ screen: import('./help-index.js').HelpScreen, item: import('./help-index.js').HelpSidebarItem } | null}
 */
export const findHelpSidebarItem = (screenId) => {
  if (!screenId) return null
  const normalized = screenId.trim().toLowerCase()
  for (const section of HELP_SECTIONS) {
    for (const screen of section.screens) {
      const item = (screen.sidebarItems || []).find((i) => i.screenId === normalized)
      if (item) return { screen, item }
    }
  }
  return null
}

/**
 * Aplana todas las pantallas (incluye subScreens anidadas).
 * @returns {import('./help-index.js').HelpScreen[]}
 */
export const flattenHelpScreens = () => {
  /** @type {import('./help-index.js').HelpScreen[]} */
  const result = []

  const walk = (/** @type {import('./help-index.js').HelpScreen} */ s) => {
    result.push(s)
    ;(s.sidebarItems || []).forEach((item) => {
      if (item.screenId) {
        result.push({
          screenId: item.screenId,
          label: item.label,
          navPath: `${s.navPath} → ${item.label}`,
          keywords: [item.label, item.description, sidebarItemSearchText(item), ...(s.keywords || [])].map(
            (k) => String(k).toLowerCase()
          ),
          status: s.status,
          summary: item.description,
          detail: item.detail,
        })
      }
    })
    ;(s.subScreens || []).forEach(walk)
  }

  HELP_SECTIONS.forEach((section) => section.screens.forEach(walk))
  return result
}

/**
 * @param {string} screenId
 * @returns {import('./help-index.js').HelpScreen | null}
 */
export const getHelpByScreenId = (screenId) => {
  if (!screenId) return null
  const normalized = screenId.trim().toLowerCase()

  const sidebarMatch = findHelpSidebarItem(normalized)
  if (sidebarMatch) {
    const { screen, item } = sidebarMatch
    return {
      screenId: item.screenId,
      label: item.label,
      navPath: `${screen.navPath} → ${item.label}`,
      keywords: screen.keywords,
      status: screen.status,
      summary: item.description,
      detail: item.detail,
    }
  }

  return flattenHelpScreens().find((s) => s.screenId === normalized) || null
}

/**
 * Oculta secciones según visibilidad del usuario.
 * HOTEL, AGRO, BODEGA y SOPORTE dependen de auth; el resto se muestra siempre.
 *
 * @param {import('./help-index.js').HelpSection[]} sections
 * @param {{ showHotel?: boolean, showAgro?: boolean, showBodegas?: boolean, showSupport?: boolean }} [options]
 */
export const filterHelpSectionsByVisibility = (sections, { showHotel = true, showAgro = true, showBodegas = true, showSupport = true } = {}) =>
  sections.filter((section) => {
    if (section.id === 'hot') return showHotel
    if (section.id === 'agr') return showAgro
    if (section.id === 'bod') return showBodegas
    if (section.id === 'sup') return showSupport
    return true
  })

/**
 * @param {string} query
 * @param {{ showHotel?: boolean, showAgro?: boolean, showBodegas?: boolean, showSupport?: boolean }} [options]
 * @returns {import('./help-index.js').HelpSection[]}
 */
export const getHelpSections = (query = '', { showHotel = true, showAgro = true, showBodegas = true, showSupport = true } = {}) => {
  const searched = searchHelpSections(query)
  return filterHelpSectionsByVisibility(searched, { showHotel, showAgro, showBodegas, showSupport })
}

/**
 * @param {string} query
 * @returns {import('./help-index.js').HelpSection[]}
 */
export const searchHelpSections = (query) => {
  const term = normalizeSearchTerm(query)
  if (!term) return HELP_SECTIONS

  return HELP_SECTIONS.map((section) => {
    const sectionMatch =
      normalizeSearchTerm(section.label).includes(term) ||
      normalizeSearchTerm(section.id).includes(term)

    const screens = section.screens.filter((s) => {
      if (sectionMatch) return true
      if (normalizeSearchTerm(s.label).includes(term)) return true
      if (normalizeSearchTerm(s.navPath).includes(term)) return true
      if (normalizeSearchTerm(s.screenId).includes(term)) return true
      if (normalizeSearchTerm(s.summary || '').includes(term)) return true
      if ((s.sidebarItems || []).some((item) => sidebarItemMatchesSearch(item, term))) return true
      return (s.keywords || []).some((kw) => normalizeSearchTerm(kw).includes(term))
    })

    return screens.length ? { ...section, screens } : null
  }).filter(Boolean)
}

/**
 * Texto plano de ayuda para una pantalla (p. ej. contexto de IA).
 * @param {string} screenId
 * @returns {string | null}
 */
export const getHelpContextText = (screenId) => {
  const help = getHelpByScreenId(screenId)
  if (!help) return null

  const lines = [
    `# Ayuda: ${help.label}`,
    `Ruta: ${help.navPath}`,
    `ID: ${help.screenId}`,
    `Estado: ${help.status}`,
    '',
  ]

  if (help.summary) {
    lines.push(help.summary, '')
  }

  if (help.sidebarItems?.length) {
    lines.push('## Opciones del sidebar')
    help.sidebarItems.forEach((item) => {
      lines.push(`### ${item.label}`)
      lines.push(item.description)
      if (item.screenId) lines.push(`ID: ${item.screenId}`)
      ;(item.detail?.sections || []).forEach((section) => {
        lines.push(`#### ${section.title}`)
        if (section.intro) lines.push(section.intro)
        ;(section.bullets || []).forEach((b) => lines.push(`- ${b}`))
        ;(section.paragraphs || []).forEach((p) => lines.push(p))
        ;(section.fields || []).forEach((field) => {
          lines.push(`- **${field.label}**: ${field.description}`)
        })
      })
      lines.push('')
    })
  }

  if (help.detail?.sections?.length) {
    help.detail.sections.forEach((section) => {
      lines.push(`## ${section.title}`)
      if (section.intro) lines.push(section.intro)
      ;(section.bullets || []).forEach((b) => lines.push(`- ${b}`))
      ;(section.paragraphs || []).forEach((p) => lines.push(p))
      ;(section.fields || []).forEach((field) => {
        lines.push(`- **${field.label}**: ${field.description}`)
      })
      lines.push('')
    })
  }

  ;(help.outline || []).forEach((section) => {
    lines.push(`## ${section.title}`)
    section.items.forEach((item) => {
      const mark = item.placeholder ? '(pendiente)' : ''
      lines.push(`- ${item.label} ${mark}`.trim())
    })
    lines.push('')
  })

  return lines.join('\n').trim()
}

/**
 * Cuenta total de pantallas documentadas / pendientes.
 * @param {{ showHotel?: boolean, showAgro?: boolean, showBodegas?: boolean, showSupport?: boolean }} [options]
 */
export const getHelpStats = ({ showHotel = true, showAgro = true, showBodegas = true, showSupport = true } = {}) => {
  const sections = filterHelpSectionsByVisibility(HELP_SECTIONS, { showHotel, showAgro, showBodegas, showSupport })
  const screens = sections.flatMap((section) => section.screens)
  return {
    total: screens.length,
    pending: screens.filter((s) => s.status === 'pending').length,
    draft: screens.filter((s) => s.status === 'draft').length,
    complete: screens.filter((s) => s.status === 'complete').length,
  }
}
